import { useState, useEffect, useCallback } from 'react'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { timetableImportApi, type MatchingReport } from '@/services/api/timetableImport'
import { extractErrorMessage } from '@/utils/errorHandler'
import { useSearchParams } from 'react-router-dom'

interface MatchRecord {
  teacher_name: string
  teacher_code: string
  code: string
  confidence: number
  matched: boolean
  requires_review: boolean
}

interface SessionMatchRecord {
  match_id: string
  parsed_code: string
  teacher_id: string | null
  teacher_name: string | null
  confidence: number
  match_method: string
  is_verified: boolean
  verified_at: string | null
  alternative_matches: unknown[]
}

export function CodeMatchingReportPage() {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('sessionId')

  const [records, setRecords] = useState<MatchRecord[]>([])
  const [sessionMatches, setSessionMatches] = useState<SessionMatchRecord[]>([])
  const [report, setReport] = useState<MatchingReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all')
  const [searchTerm, setSearchTerm] = useState('')

  const fetchReport = useCallback(async () => {
    setLoading(true)
    try {
      if (sessionId) {
        const [matchesData, reportData] = await Promise.all([
          timetableImportApi.getCodeMatches(sessionId),
          timetableImportApi.getMatchingReport(sessionId),
        ])
        setSessionMatches(matchesData)
        setReport(reportData)
      } else {
        const res = await handleApiRequest<MatchRecord[]>(apiClient.get('/api/v1/teacher-code/report'))
        setRecords(res)
      }
      setError(null)
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Matching-Report konnte nicht geladen werden' }))
    } finally { setLoading(false) }
  }, [sessionId])

  useEffect(() => { fetchReport() }, [fetchReport])

  const isSession = !!sessionId
  const displayRecords = isSession ? sessionMatches : records

  const filtered = displayRecords.filter((r: any) => {
    const conf = isSession ? r.confidence : r.confidence * 100
    const level = conf >= 80 ? 'high' : conf >= 50 ? 'medium' : 'low'
    const matchesFilter = filter === 'all' || level === filter
    const name = r.teacher_name || ''
    const code = r.parsed_code || r.code || ''
    const matchesSearch = !searchTerm || name.toLowerCase().includes(searchTerm.toLowerCase()) || code.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesFilter && matchesSearch
  })

  const stats = isSession
    ? {
        total: report?.total_matches || 0,
        high: report?.high_confidence || 0,
        medium: (report?.total_matches || 0) - (report?.high_confidence || 0) - (report?.low_confidence || 0),
        low: report?.low_confidence || 0,
      }
    : {
        total: records.length,
        high: records.filter(r => r.confidence >= 0.8).length,
        medium: records.filter(r => r.confidence >= 0.5 && r.confidence < 0.8).length,
        low: records.filter(r => r.confidence < 0.5).length,
      }

  if (loading) return <div className="p-6 text-gray-500">Laden...</div>
  if (error) return <div className="p-6 text-red-600">{error}</div>

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-2xl font-bold mb-6">
        Code-Matching-Report
        {isSession && <span className="text-sm font-normal text-gray-500 ml-2">(Session)</span>}
      </h1>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
          <div className="text-xs text-gray-600">Gesamt</div>
        </div>
        <div className="bg-green-50 border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-700">{stats.high}</div>
          <div className="text-xs text-gray-600">High Confidence</div>
        </div>
        <div className="bg-yellow-50 border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-700">{stats.medium}</div>
          <div className="text-xs text-gray-600">Medium Confidence</div>
        </div>
        <div className="bg-red-50 border rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-700">{stats.low}</div>
          <div className="text-xs text-gray-600">Low Confidence</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <select value={filter} onChange={e => setFilter(e.target.value as typeof filter)}
          className="px-3 py-2 border rounded-lg text-sm">
          <option value="all">Alle</option>
          <option value="high">High (&gt;= 80%)</option>
          <option value="medium">Medium (50-79%)</option>
          <option value="low">Low (&lt; 50%)</option>
        </select>
        <input
          type="text" placeholder="Suche..." value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 border rounded-lg text-sm"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lehrer</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Methode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Keine Treffer</td></tr>
            ) : filtered.map((r: any, i) => {
              const conf = r.confidence
              const level = isSession
                ? (conf >= 80 ? 'high' : conf >= 50 ? 'medium' : 'low')
                : (conf >= 0.8 ? 'high' : conf >= 0.5 ? 'medium' : 'low')
              const normalizedConf = isSession ? conf : conf * 100
              return (
                <tr key={i} className={`hover:bg-gray-50 ${level === 'low' ? 'bg-red-50/50' : ''}`}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.teacher_name || '—'}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-600">{r.parsed_code || r.code}</td>
                  <td className="px-4 py-3 text-sm">
                    {r.match_method && (
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        r.match_method === 'pattern' ? 'bg-purple-100 text-purple-700' :
                        r.match_method === 'fuzzy' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {r.match_method}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${level === 'high' ? 'bg-green-500' : level === 'medium' ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${normalizedConf}%` }} />
                      </div>
                      <span className="text-xs text-gray-500">{Math.round(normalizedConf)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {isSession
                      ? (r.is_verified
                        ? <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Verifiziert</span>
                        : <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">Pending</span>)
                      : (level === 'high' && <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Gematcht</span>)}
                    {level === 'medium' && <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">Pruefen</span>}
                    {level === 'low' && <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">Manuell</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
